import React from "react";
import { Clock, Package } from "lucide-react";
import type { RegistrationOptions } from "../../api/eventAddonsAPI";
import { addonsTotal } from "../../api/eventAddonsAPI";
import { formatClubInstant } from "../../utils/clubTime";

/**
 * The family's choice of time slot and add-ons on an event registration form
 * (PM 4e4384e4: Chris's photo day, and extras on any event). Used by the
 * public sign-up page and the member registration modal.
 *
 * Shows prices, never a capacity: a full slot is simply not offered (the
 * server leaves it out of registration-options), and the server prices
 * whatever is picked.
 */

interface Props {
  options: RegistrationOptions;
  sessionId: string;
  onSessionChange: (id: string) => void;
  addonIds: string[];
  onAddonsChange: (ids: string[]) => void;
  /** Dark styling for the member modal in dark mode. */
  isDarkMode?: boolean;
}

const money = (n: number) => `$${Number(n).toFixed(2)}`;

export function slotTime(slot: { start_at: string | null; end_at: string | null }): string {
  if (!slot.start_at) return "";
  const start = formatClubInstant(slot.start_at, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  const end = slot.end_at ? formatClubInstant(slot.end_at, { hour: "numeric", minute: "2-digit" }) : "";
  return end ? `${start} – ${end}` : start;
}

/** Whether the form's choices satisfy the event's rules; returns the problem, or null. */
export function extrasProblem(options: RegistrationOptions | null, sessionId: string, addonIds: string[]): string | null {
  if (!options) return null;
  if (options.session_signup) {
    if (options.time_slots.length === 0) return "No times are available for this event.";
    if (!sessionId) return "Please choose a time.";
  }
  if (options.addon_required && options.addons.length > 0 && addonIds.length === 0) {
    return options.addon_selection === "one" ? "Please choose an option." : "Please choose at least one option.";
  }
  return null;
}

/** Does this event ask for anything beyond a plain registration? */
export function usesExtras(options: RegistrationOptions | null): boolean {
  return !!options && (options.session_signup || options.addons.length > 0);
}

const EventExtrasPicker: React.FC<Props> = ({ options, sessionId, onSessionChange, addonIds, onAddonsChange, isDarkMode = false }) => {
  const one = options.addon_selection === "one";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const text = isDarkMode ? "text-gray-100" : "text-gray-900";
  const box = (selected: boolean) =>
    `flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
      selected
        ? "border-[var(--cc-action)] bg-gray-50 dark:bg-gray-700/50"
        : isDarkMode
          ? "border-gray-600 hover:border-gray-500"
          : "border-gray-200 hover:border-gray-300"
    }`;

  const toggleAddon = (id: string) => {
    if (one) {
      onAddonsChange(addonIds.includes(id) && !options.addon_required ? [] : [id]);
      return;
    }
    onAddonsChange(addonIds.includes(id) ? addonIds.filter((x) => x !== id) : [...addonIds, id]);
  };

  const total = addonsTotal(options.addons, addonIds);

  return (
    <div className="space-y-6">
      {options.session_signup && (
        <fieldset>
          <legend className={`flex items-center gap-2 text-base font-semibold mb-3 ${text}`}>
            <Clock className="w-4 h-4" /> Choose a time <span className="text-red-500">*</span>
          </legend>
          {options.time_slots.length === 0 ? (
            <p className={`text-sm ${muted}`}>No times are available for this event.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {options.time_slots.map((slot) => (
                <label key={slot.id} className={box(sessionId === slot.id)}>
                  <input
                    type="radio"
                    name="event-time-slot"
                    value={slot.id}
                    checked={sessionId === slot.id}
                    onChange={() => onSessionChange(slot.id)}
                    className="mt-1 accent-[var(--cc-action)]"
                  />
                  <span>
                    <span className={`block font-medium ${text}`}>{slot.name}</span>
                    {slotTime(slot) && <span className={`block text-sm ${muted}`}>{slotTime(slot)}</span>}
                  </span>
                </label>
              ))}
            </div>
          )}
        </fieldset>
      )}

      {options.addons.length > 0 && (
        <fieldset>
          <legend className={`flex items-center gap-2 text-base font-semibold mb-1 ${text}`}>
            <Package className="w-4 h-4" /> {one ? "Choose an option" : "Add extras"}
            {options.addon_required && <span className="text-red-500">*</span>}
          </legend>
          {!one && <p className={`text-sm mb-3 ${muted}`}>Pick any you would like.</p>}
          <div className="space-y-2 mt-2">
            {options.addons.map((addon) => {
              const selected = addonIds.includes(addon.id);
              return (
                <label key={addon.id} className={box(selected)}>
                  <input
                    type={one ? "radio" : "checkbox"}
                    name="event-addon"
                    value={addon.id}
                    checked={selected}
                    onChange={() => toggleAddon(addon.id)}
                    className="mt-1 accent-[var(--cc-action)]"
                  />
                  <span className="flex-1">
                    <span className={`flex justify-between gap-3 font-medium ${text}`}>
                      <span>{addon.label}</span>
                      <span>{money(addon.amount)}</span>
                    </span>
                    {addon.description && <span className={`block text-sm ${muted}`}>{addon.description}</span>}
                  </span>
                </label>
              );
            })}
          </div>
          {total > 0 && (
            <p className={`mt-3 text-sm font-medium ${text}`} data-testid="addons-total">
              Options: {money(total)}
            </p>
          )}
        </fieldset>
      )}
    </div>
  );
};

export default EventExtrasPicker;
